import { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Alert, Spinner, Table } from 'react-bootstrap';
import dayjs from 'dayjs';

import { http } from '../../features/jobs/api/http';
import ImpactPreview from './ImpactPreview';

const fmt2 = v => (v == null ? '-' : Number(v).toFixed(2));
const hhmm = ts => (ts ? dayjs(Number(ts)).format('HH:mm') : '-');

// Cutting one recorded drum in two.
//
// When a drum change goes undetected the machine does not stop — it winds the
// next bobbin onto the same open token, and what gets recorded is one execution
// where there were two.
//
// WHERE THE REST GOES has two answers, and for a long time only one was offered:
//
//   · to whatever ran NEXT on the machine. Right when a boundary slipped — the
//     metres really are the next drum's, and the whole run can be walked back
//     one deliberate step at a time (helpers/drumChain.js does the chain).
//
//   · it is a DRUM OF ITS OWN. Right when a whole bobbin was wound unnoticed:
//     the operator took a drum off, put an empty one on, and the line carried on
//     counting. Those metres are not the next drum's — they are a bobbin with no
//     record at all, and the product that made them is usually the one STILL
//     RUNNING, which has no execution to receive anything.
//
// That second case had nowhere to go: Execution History lists finished work, so
// the live product could not be named. It is named here instead, from the job
// list — a product is a job whether or not it has finished a drum.
//
// The numbers arrive pre-filled from the diameter setpoint (the moment it
// changed, and the metres produced after it), but they are only a proposal: the
// operator is the one who knows what was physically on the machine, and every
// field here is editable. Nothing is written until the preview has been seen.
const SplitExecutionModal = ({ show, onHide, log, machineId, suggestion, onDone }) => {
  const [keepLength, setKeepLength] = useState('');
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // 'next' — the chain hands the rest on. 'own' — the rest is its own bobbin.
  const [destination, setDestination] = useState('next');
  const [targetJobId, setTargetJobId] = useState('');
  const [products, setProducts] = useState([]);
  // Whether the source keeps paying for the cable this drum was made from.
  // There is no right default, so the default changes nothing.
  const [inputPolicy, setInputPolicy] = useState('keep');

  const splitAt = suggestion?.splitAt ?? null;

  useEffect(() => {
    if (!show) return;
    setErr('');
    setPreview(null);
    setReason('');
    setDestination('next');
    setTargetJobId('');
    setInputPolicy('keep');
    setKeepLength(
      suggestion?.keepLengthEstimate != null
        ? String(suggestion.keepLengthEstimate)
        : ''
    );
  }, [show, suggestion]);

  // Every product, not only the ones that finished a drum on this machine — the
  // whole point of the "own drum" route is reaching one that has not. Active and
  // queued products are jobs like any other, so they are simply in this list.
  useEffect(() => {
    if (!show) return;
    let alive = true;
    http
      .get('/api/redis/jobs')
      .then(({ data: d }) => {
        if (!alive) return;
        const rows = Array.isArray(d) ? d : d?.jobs || d?.data || [];
        setProducts(
          rows
            .map(j => ({
              jobId: String(j.jobId || j._id || ''),
              productLabel: j.productLabel || j.jobCode || j.jobId,
              status: String(j.status || '').toUpperCase()
            }))
            .filter(j => j.jobId && j.jobId !== String(log?.jobId))
        );
      })
      .catch(() => setProducts([]));
    return () => {
      alive = false;
    };
  }, [show, log?.jobId]);

  const parsed = Number(String(keepLength).replace(',', '.'));
  const recorded = Number(log?.producedLength);
  const inRange = Number.isFinite(parsed) && parsed >= 0 && parsed < recorded;
  const rest = inRange ? Math.round((recorded - parsed) * 1000) / 1000 : 0;

  // Keeping nothing is not a cut, it is a move: the drum was never this
  // product's at all. `split-drum` refuses a keep length of zero — correctly,
  // there is no first drum — so that case reassigns the drum itself.
  const wholeDrum = destination === 'own' && inRange && parsed === 0;

  const valid = inRange && (destination === 'next' || Boolean(targetJobId));

  const targetLabel = useMemo(
    () => products.find(p => p.jobId === targetJobId)?.productLabel || 'the chosen product',
    [products, targetJobId]
  );

  const url = path => `/api/redis/jobs/${encodeURIComponent(log.jobId)}/${path}`;
  const moveBody = (tokenId, dryRun) => ({
    tokenId,
    targetJobId,
    inputPolicy,
    reason: reason || 'drum change not registered',
    dryRun
  });

  const fail = e => {
    const d = e?.response?.data;
    return (
      (d?.blockers && d.blockers.join(' · ')) ||
      (d?.errors && d.errors.join('; ')) ||
      d?.error ||
      e?.message ||
      'Failed.'
    );
  };

  const call = async dryRun => {
    setErr('');
    setBusy(true);
    try {
      // ── the rest goes to whatever ran next ──
      if (destination === 'next') {
        const { data } = await http.post('/api/redis/jobs/split-execution', {
          machineId,
          jobId: log.jobId,
          tokenId: log.tokenId,
          keepLength: parsed,
          ...(splitAt ? { splitAt } : {}),
          reason: reason || 'drum change not registered',
          dryRun
        });
        if (dryRun) setPreview({ kind: 'next', ...data });
        else {
          onDone?.(data);
          onHide?.();
        }
        return;
      }

      // ── the rest is a drum of its own ──
      if (wholeDrum) {
        const { data } = await http.post(url('reassign-drum'), moveBody(log.tokenId, dryRun));
        if (dryRun) setPreview({ kind: 'move', ...data });
        else {
          onDone?.(data);
          onHide?.();
        }
        return;
      }

      if (dryRun) {
        // Only the cut can be previewed against real data: the drum the move
        // would carry does not exist until the cut is applied. Said plainly
        // below rather than faked.
        const { data } = await http.post(url('split-drum'), {
          tokenId: log.tokenId,
          keepLength: parsed,
          ...(splitAt ? { splitAt } : {}),
          reason: reason || 'drum change not registered',
          dryRun: true
        });
        setPreview({ kind: 'own', ...data });
        return;
      }

      // Two writes. The cut first, because the move needs a drum to carry.
      const { data: cut } = await http.post(url('split-drum'), {
        tokenId: log.tokenId,
        keepLength: parsed,
        ...(splitAt ? { splitAt } : {}),
        reason: reason || 'drum change not registered',
        dryRun: false
      });
      const newId = cut.created?.tokenId;

      try {
        const { data: move } = await http.post(url('reassign-drum'), moveBody(newId, false));
        onDone?.({ ...cut, move: move.move });
        onHide?.();
      } catch (e) {
        // Half-done, and recoverable — but only if it is said. The drum exists
        // and is sitting on the product it was cut from.
        setErr(
          `The cut landed: ${newId} holds ${fmt2(cut.rest)} m on ${log.productLabel}. ` +
            `Moving it to ${targetLabel} failed — ${fail(e)}. ` +
            `Move it from the Drum Ledger, or from this run's drums.`
        );
        setPreview(null);
      }
    } catch (e) {
      setErr(fail(e));
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  if (!log) return null;

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="fs-8">Split this execution</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="fs-9 text-700 mb-3">
          <b>{log.productLabel}</b> · {log.color || '-'} ·{' '}
          <span className="font-monospace">{log.tokenId}</span>
          <br />
          recorded <b>{fmt2(log.producedLength)} m</b> over{' '}
          {hhmm(log.startedAt)}–{hhmm(log.completedAt)}
        </div>

        {suggestion?.kind === 'CHANGED_MID_DRUM' && (
          <Alert variant="warning" className="py-2 fs-9">
            The diameter setpoint changed from <b>Ø{suggestion.fromDiameter}</b> to{' '}
            <b>Ø{suggestion.toDiameter}</b> at <b>{hhmm(suggestion.splitAt)}</b> — the
            line was making a different product from there on. About{' '}
            <b>{suggestion.metresAfterEstimate.toLocaleString()} m</b> were produced
            after that moment.
            <div className="fs-10 mt-1">
              That figure is integrated from the speed samples, not read off the
              meter — check it against what came off the machine.
            </div>
          </Alert>
        )}

        <Form.Group className="mb-3" style={{ maxWidth: 320 }}>
          <Form.Label className="fs-9 fw-semibold">
            This drum really held (m)
          </Form.Label>
          <Form.Control
            type="text"
            inputMode="decimal"
            value={keepLength}
            isInvalid={keepLength !== '' && !inRange}
            onChange={e => {
              setKeepLength(e.target.value);
              setPreview(null);
            }}
          />
          {rest > 0 && (
            <Form.Text className="fs-10">
              {fmt2(rest)} m have to go somewhere.
            </Form.Text>
          )}
        </Form.Group>

        {/* The question, asked out loud. It used to have one answer and the
            other case — a whole bobbin wound unnoticed — had nowhere to go. */}
        <div className="mb-3 p-3 border border-200 rounded">
          <Form.Label className="fs-9 fw-semibold mb-2">
            Where do {rest > 0 ? `${fmt2(rest)} m` : 'the rest'} go?
          </Form.Label>

          <Form.Check
            type="radio"
            name="splitDestination"
            id="splitDest-next"
            className="fs-9"
            checked={destination === 'next'}
            onChange={() => {
              setPreview(null);
              setDestination('next');
            }}
            label={
              <>
                To whatever ran <b>next</b> on {String(machineId).toUpperCase()}
                <div className="fs-10 text-700">
                  A boundary slipped — the metres really are the next drum&apos;s. The
                  run can then be walked back one drum at a time in the Drum Ledger.
                </div>
              </>
            }
          />

          <Form.Check
            type="radio"
            name="splitDestination"
            id="splitDest-own"
            className="fs-9 mt-2"
            checked={destination === 'own'}
            onChange={() => {
              setPreview(null);
              setDestination('own');
            }}
            label={
              <>
                They are a <b>drum of their own</b>, belonging to another product
                <div className="fs-10 text-700">
                  A whole bobbin was wound and never registered. The product that
                  made them does not need to have finished anything — one that is
                  <b> active or queued right now</b> is in the list below.
                </div>
              </>
            }
          />

          {destination === 'own' && (
            <div className="mt-3">
              <Form.Group className="mb-3">
                <Form.Label className="fs-10 mb-1">
                  {wholeDrum
                    ? 'The whole drum belongs to'
                    : `The ${fmt2(rest)} m belong to`}
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={targetJobId}
                  onChange={e => {
                    setPreview(null);
                    setTargetJobId(e.target.value);
                  }}
                >
                  <option value="">— choose the product that really made them —</option>
                  {products.map(p => (
                    <option key={p.jobId} value={p.jobId}>
                      {p.productLabel}
                      {p.status === 'ACTIVE' ? ' · running now' : ''}
                      {p.status === 'PENDING' ? ' · queued' : ''}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              {wholeDrum && (
                <Alert variant="info" className="py-2 fs-10">
                  Keeping nothing is not a cut — the drum was never{' '}
                  {log.productLabel}&apos;s at all, so the drum itself moves.
                </Alert>
              )}

              {/* The question the ledger could never answer. This drum ate metres
                  off an input drum belonging to the SOURCE product; whether that
                  was right depends on what physically happened. */}
              <Form.Label className="fs-10 mb-1">
                The cable it was made from belongs to {log.productLabel}
              </Form.Label>
              <Form.Check
                type="radio"
                name="splitInputPolicy"
                id="splitInput-keep"
                className="fs-9"
                checked={inputPolicy === 'keep'}
                onChange={() => {
                  setPreview(null);
                  setInputPolicy('keep');
                }}
                label={`${log.productLabel} really used those metres — leave its input drums alone`}
              />
              <Form.Check
                type="radio"
                name="splitInputPolicy"
                id="splitInput-giveback"
                className="fs-9"
                checked={inputPolicy === 'give-back'}
                onChange={() => {
                  setPreview(null);
                  setInputPolicy('give-back');
                }}
                label="They were only credited here by mistake — give the input drums their metres back"
              />
            </div>
          )}
        </div>

        <Form.Group className="mb-3">
          <Form.Label className="fs-9 fw-semibold">Why (kept on the drum)</Form.Label>
          <Form.Control
            size="sm"
            value={reason}
            placeholder="drum change not registered"
            onChange={e => setReason(e.target.value)}
          />
        </Form.Group>

        {err && (
          <Alert variant="danger" className="py-2 fs-9 mb-3">
            {err}
          </Alert>
        )}

        {preview?.kind === 'next' && (
          <>
            <div className="fs-9 fw-semibold mb-1">What this would do</div>
            <Table size="sm" className="fs-10 mb-2">
              <thead>
                <tr className="text-700">
                  <th className="fw-medium">Product</th>
                  <th className="fw-medium text-end">From</th>
                  <th className="fw-medium text-end">To</th>
                  <th className="fw-medium">Window</th>
                </tr>
              </thead>
              <tbody>
                {[preview.cut, preview.receives].map(side => (
                  <tr key={side.tokenId + side.jobId}>
                    <td>
                      {side.productLabel}
                      <div className="text-700 font-monospace">{side.tokenId}</div>
                    </td>
                    <td className="text-end font-monospace">{fmt2(side.from)} m</td>
                    <td className="text-end font-monospace fw-semibold">
                      {fmt2(side.to)} m
                    </td>
                    <td className="font-monospace">
                      {hhmm(side.window.from)}–{hhmm(side.window.to)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <div className="fs-10 text-700">
              {fmt2(preview.remainder)} m move. The window and each operator&apos;s
              share move with them, so the speeds stay true.
            </div>

            {/* Not a count. A drum length reaches the task, its material, the
                parent drum's consumption, the job summary and two Mongo
                collections — all of it listed before anything is written. */}
            {(preview.sides || []).map(side => (
              <div
                key={`${side.jobId}-${side.tokenId}`}
                className="mt-3 pt-2 border-top"
              >
                <ImpactPreview row={side} />
              </div>
            ))}
            {preview.warnings?.length > 0 && (
              <Alert variant="warning" className="py-2 fs-10 mt-2 mb-0">
                {preview.warnings.map((w, i) => (
                  <div key={i}>{w}</div>
                ))}
              </Alert>
            )}
          </>
        )}

        {preview?.kind === 'own' && (
          <>
            <div className="fs-9 fw-semibold mb-1">What this would do</div>
            <div className="fs-10">
              <div>
                <span className="font-monospace">{preview.source?.tokenId}</span> keeps{' '}
                <b>{fmt2(preview.source?.producedTo)} m</b> on {log.productLabel} — it
                was {fmt2(preview.source?.producedFrom)} m.
              </div>
              <div className="mt-1">
                A new finished drum{' '}
                <span className="font-monospace">{preview.created?.tokenId}</span> takes{' '}
                <b>{fmt2(preview.rest)} m</b>
                {preview.created?.drum_diameter
                  ? ` on ${preview.created.drum_material || ''} Ø${preview.created.drum_diameter}`
                  : ''}
                , then moves to <b>{targetLabel}</b>.
              </div>
              <div className="mt-2 text-700">
                Two writes, in that order — the move needs a drum to carry. Only the
                cut can be checked against real data here: the drum the move would
                carry does not exist until the cut is applied. If the move then
                fails, the new drum is left on {log.productLabel} and can be moved
                from the Drum Ledger.
              </div>
            </div>
            {preview.warnings?.length > 0 && (
              <Alert variant="warning" className="py-2 fs-10 mt-2 mb-0">
                {preview.warnings.map((w, i) => (
                  <div key={i}>{w}</div>
                ))}
              </Alert>
            )}
          </>
        )}

        {preview?.kind === 'move' && preview.move && (
          <>
            <div className="fs-9 fw-semibold mb-1">What this would do</div>
            <div className="fs-10">
              <div>
                <span className="font-monospace">{preview.move.tokenId}</span> —{' '}
                <b>{fmt2(preview.move.producedLength)} m</b> — {preview.move.from} →{' '}
                <b>{preview.move.to}</b>
              </div>
              <div className="mt-1 text-700">
                {preview.move.mode === 'split'
                  ? `Its run keeps ${preview.move.remainingOnSource?.length || 0} other drum(s).`
                  : 'Its run carried nothing else, so the whole run moves with it.'}
              </div>
              {(preview.inputs || []).map(inp => (
                <div key={inp.tokenId} className="text-700">
                  {inp.tokenId}:{' '}
                  {inp.wouldGiveBack
                    ? `${fmt2(inp.wouldGiveBack)} m given back to ${preview.move.from}`
                    : `keeps its ${fmt2(inp.consumedLength)} m consumed on ${preview.move.from}`}
                </div>
              ))}
            </div>
            {preview.warnings?.length > 0 && (
              <Alert variant="warning" className="py-2 fs-10 mt-2 mb-0">
                {preview.warnings.map((w, i) => (
                  <div key={i}>{w}</div>
                ))}
              </Alert>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="phoenix-secondary" onClick={onHide} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="outline-primary"
          disabled={!valid || busy}
          onClick={() => call(true)}
        >
          {busy && !preview ? <Spinner animation="border" size="sm" /> : 'Preview'}
        </Button>
        <Button
          variant="primary"
          disabled={!valid || busy || !preview}
          onClick={() => call(false)}
          title={!preview ? 'Preview it first' : 'Apply'}
        >
          {destination === 'own' && !wholeDrum ? 'Cut and move' : 'Apply'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SplitExecutionModal;
